import { Box } from '@mui/system';
import { Button } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import Swal from 'sweetalert2';
import {
  currentUploadStep,
  stepsUpload,
  selectedNewSites,
  selectedUpdatedSites,
  selectedDeletedSites,
  openReviewForFile,
  ranDbFileUploadResponse,
  openUploadFileRanDb,
  allSitesRanDb,
} from '../../common/atom/atoms';
import { reviewBulkAddUploadDeleteSites } from './functions/review_bulk_add_upload_delete_sites_ran_db.function';
import { getAllRanSites } from './functions/get_all_ran_sites.function';
import { notify } from '../../common/functions/react-toastify.function';
import { useBackdrop } from '../../common/components/backDrop_context.component';

export default function NextAndBackButtonForUpload({ onUploadComplete }) {
  const [currentStep, setCurrentStep] = useAtom(currentUploadStep);
  const steps = useAtomValue(stepsUpload);
  const [selectedNewSitesData, setSelectedNewSitesData] = useAtom(selectedNewSites);
  const [selectedUpdatedSitesData, setSelectedUpdatedSitesData] = useAtom(selectedUpdatedSites);
  const [selectedDeletedSitesData, setSelectedDeletedSitesData] = useAtom(selectedDeletedSites);
  const setOpenReviewForFile = useSetAtom(openReviewForFile);
  const setOpenUploadFile = useSetAtom(openUploadFileRanDb);
  const setAllRanSite = useSetAtom(allSitesRanDb);
  const [ranDbFileUploadResponseData, setRanDbFileUploadResponseData] =
    useAtom(ranDbFileUploadResponse);
  const { showBackdrop, hideBackdrop } = useBackdrop();

  const handleNextStep = () => {
    // Store selected rows based on current step
    if (currentStep === 0) {
      // For new sites, get selected rows from the table
      const newSitesData = ranDbFileUploadResponseData?.newSites || [];
      const selectedRows = Object.keys(selectedNewSitesData).map(
        (key) => newSitesData[parseInt(key)]
      );
      setSelectedNewSitesData(selectedRows);
    } else if (currentStep === 1) {
      // For updated sites, get selected rows from the table
      const updatedSitesData = ranDbFileUploadResponseData?.updatedSites || [];
      const selectedRows = Object.keys(selectedUpdatedSitesData).map(
        (key) => updatedSitesData[parseInt(key)]
      );
      setSelectedUpdatedSitesData(selectedRows);
    }

    // Move to next step
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleConfirmUpload = async () => {
    // Validate that at least one item is selected in any category
    const hasNewSites = selectedNewSitesData && selectedNewSitesData.length > 0;
    const hasUpdatedSites = selectedUpdatedSitesData && selectedUpdatedSitesData.length > 0;
    const hasDeletedSites = Object.keys(selectedDeletedSitesData).length > 0;

    if (!hasNewSites && !hasUpdatedSites && !hasDeletedSites) {
      notify(
        'error',
        'Please select at least one item from new sites, updated sites, or deleted sites to proceed.'
      );
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      didOpen: (popup) => {
        // Popup itself
        popup.style.zIndex = 99999;
        // Backdrop container
        const swalContainer = document.querySelector('.swal2-container');
        if (swalContainer) {
          swalContainer.style.zIndex = 99999;
        }
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          showBackdrop();

          // Get selected deleted sites
          const deletedSitesData = ranDbFileUploadResponseData?.deletedSites || [];
          const selectedDeletedRows = Object.keys(selectedDeletedSitesData).map(
            (key) => deletedSitesData[parseInt(key)]
          );
          setSelectedDeletedSitesData(selectedDeletedRows);

          // Prepare the payload
          const payload = {
            newSites: selectedNewSitesData,
            updatedSites: selectedUpdatedSitesData,
            deletedSites: selectedDeletedRows,
          };

          // Make API call
          const response = await reviewBulkAddUploadDeleteSites(payload);

          if (response) {
            notify('Upload completed successfully!', 'success');

            // Close the upload modal
            setOpenUploadFile(false);

            // Refresh table and dashboard (correct pagination + totalCount) via parent callback
            if (onUploadComplete && typeof onUploadComplete === 'function') {
              try {
                await onUploadComplete();
              } catch (error) {
                console.error('Error in upload complete callback:', error);
                // Fallback: refresh list so count at least updates
                try {
                  const filters = { archived: false };
                  const resp = await getAllRanSites(filters);
                  setAllRanSite(resp?.data || []);
                } catch (fallbackErr) {
                  console.error('Error refreshing RanDB sites:', fallbackErr);
                }
              }
            }

            // Reset all state including file upload response
            handleClose();
          }
        } catch (error) {
          console.error('Upload failed:', error);
          notify('Upload failed. Please try again.', 'error');
        } finally {
          hideBackdrop();
        }
      }
    });
  };

  const handleClose = () => {
    // Clear all selections and reset state
    setSelectedNewSitesData({});
    setSelectedUpdatedSitesData({});
    setSelectedDeletedSitesData({});
    setCurrentStep(0);
    setOpenReviewForFile(false);
    // Clear the file upload response state
    setRanDbFileUploadResponseData(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 2,
        mt: 2,
      }}
    >
      <Button
        onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
        disabled={currentStep === 0}
        variant="outlined"
      >
        Back
      </Button>
      {currentStep === steps.length - 1 ? (
        <Button
          onClick={handleConfirmUpload}
          variant="contained"
          sx={{ backgroundColor: '#BD1D23' }}
          disabled={
            (!selectedNewSitesData || selectedNewSitesData.length === 0) &&
            (!selectedUpdatedSitesData || selectedUpdatedSitesData.length === 0) &&
            Object.keys(selectedDeletedSitesData).length === 0
          }
        >
          Confirm Upload
        </Button>
      ) : (
        <Button onClick={handleNextStep} variant="contained" sx={{ backgroundColor: '#BD1D23' }}>
          {currentStep === 0 ? `Add & Next` : `Update & Next`}
        </Button>
      )}
    </Box>
  );
}
